/**
 * Shared numbers and palette for the Kae Kente weave.
 *
 * On authenticity: these are colour bands in the kente idiom, deliberately
 * NOT reproductions of named cloths. Named patterns carry real meaning and
 * some are tied to royalty and specific occasions, so we take the weaving
 * motion and the palette and leave the naming to people who hold that
 * tradition (design note, 2026-09-04).
 */

export interface BandColour {
  key: string;
  /** Plain-language name — also the button label, so the sequence is
   *  readable without relying on colour. */
  label: string;
  hex: string;
  /** Second channel for colour-vision deficiency: how many motif ticks are
   *  woven into the band. Around 8% of men cannot separate red from green,
   *  and a colour-memory game is the wrong place to ignore that. */
  ticks: number;
}

export const BAND_COLOURS: BandColour[] = [
  { key: "gold", label: "Gold", hex: "#FFCC08", ticks: 1 },
  { key: "green", label: "Green", hex: "#1B7A3E", ticks: 2 },
  { key: "red", label: "Red", hex: "#C8102E", ticks: 3 },
  { key: "blue", label: "Blue", hex: "#1668B3", ticks: 4 },
];

/** World units. The strip hangs vertically, bands stack downwards. */
export const STRIP_WIDTH = 3;
export const BAND_HEIGHT = 0.52;
export const BAND_GAP = 0.03;

/** How long one band takes to be woven across the strip, and the gap before
 *  the next one starts. Together these set the rhythm the player has to
 *  remember to. */
export const BAND_WEAVE_MS = 300;
export const BAND_INTERVAL_MS = 420;

/** Longest sequence we design for — working-memory span tops out around
 *  7±2, so the camera never needs to fit more than this. */
export const MAX_BANDS = 9;

/** Rows the camera frames at minimum. Framing all MAX_BANDS rows every round
 *  left a 3-band strip floating in a third of the board; framing exactly the
 *  round's span would rescale the bands on every step up. Framing at least
 *  this many keeps the early rounds full and the common spans identical in
 *  size. */
export const MIN_FRAMED_ROWS = 5;

export function weaveDurationMs(bandCount: number): number {
  return (bandCount - 1) * BAND_INTERVAL_MS + BAND_WEAVE_MS;
}
